<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("cod_rejections.title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button size="md" type="neutral" @click="loadList">
                        <i class="fa fa-sync"></i>
                        {{ $t("cod_rejections.btn_refresh") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="row">
                        <div class="col">
                            <h3 class="mb-0">{{ $t("cod_rejections.title") }}</h3>
                            <p class="text-sm text-muted mb-0">
                                {{ $t("cod_rejections.subtitle", { threshold: threshold }) }}
                            </p>
                        </div>
                    </div>
                </template>

                <div v-loading="loading">
                    <el-table :data="list" row-key="phone" header-row-class-name="thead-light">
                        <el-table-column type="expand">
                            <template slot-scope="props">
                                <div class="p-3">
                                    <el-table :data="props.row.orders" size="mini" border>
                                        <el-table-column
                                            :label="$t('cod_rejections.col_invoice')"
                                            prop="order_no"
                                            min-width="140"
                                        >
                                            <template slot-scope="s">
                                                {{ s.row.order_no || s.row.uuid }}
                                            </template>
                                        </el-table-column>
                                        <el-table-column
                                            :label="$t('cod_rejections.col_created')"
                                            min-width="150"
                                        >
                                            <template slot-scope="s">{{ fmtDate(s.row.created_at) }}</template>
                                        </el-table-column>
                                        <el-table-column
                                            :label="$t('cod_rejections.col_rejected')"
                                            min-width="150"
                                        >
                                            <template slot-scope="s">{{ fmtDate(s.row.rejected_at) }}</template>
                                        </el-table-column>
                                    </el-table>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column :label="$t('cod_rejections.col_phone')" prop="phone" min-width="130" />
                        <el-table-column :label="$t('cod_rejections.col_member')" min-width="160">
                            <template slot-scope="s">
                                {{ s.row.member_name || $t("cod_rejections.guest") }}
                            </template>
                        </el-table-column>
                        <el-table-column
                            :label="$t('cod_rejections.col_count')"
                            prop="count"
                            align="center"
                            min-width="90"
                        >
                            <template slot-scope="s">
                                <span class="font-weight-bold">{{ s.row.count }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('cod_rejections.col_last')" min-width="150">
                            <template slot-scope="s">{{ fmtDate(s.row.last_rejected_at) }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('cod_rejections.col_status')" align="center" min-width="110">
                            <template slot-scope="s">
                                <badge :type="s.row.blocked ? 'danger' : 'success'">
                                    {{ s.row.blocked ? $t("cod_rejections.blocked") : $t("cod_rejections.ok") }}
                                </badge>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('cod_rejections.col_action')" align="center" min-width="130">
                            <template slot-scope="s">
                                <base-button
                                    v-if="isAdmin"
                                    size="sm"
                                    type="warning"
                                    @click="confirmClear(s.row)"
                                >
                                    <i class="fa fa-eraser"></i>
                                    {{ $t("cod_rejections.btn_clear") }}
                                </base-button>
                            </template>
                        </el-table-column>
                    </el-table>

                    <div v-if="!loading && list.length === 0" class="text-center text-muted py-5">
                        {{ $t("cod_rejections.empty") }}
                    </div>
                </div>
            </card>
        </div>
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters } from "vuex";
import swal from "sweetalert2";
import { Table, TableColumn } from "element-ui";
import MemberProvider from "@/resource/member";

const member = new MemberProvider();

export default {
    name: "MemberCodRejections",
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn
    },
    data() {
        return {
            loading: false,
            list: [],
            threshold: 0
        };
    },
    computed: {
        ...mapGetters({
            isAdmin: "Auth/isAdmin"
        })
    },
    mounted() {
        this.loadList();
    },
    methods: {
        fmtDate(v) {
            if (!v) return "-";
            const d = new Date(v);
            if (isNaN(d.getTime())) return "-";
            return d.toLocaleString("th-TH", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit"
            });
        },
        async loadList() {
            this.loading = true;
            try {
                const res = await member.CodRejectionsList();
                const data = (res.data && res.data.data) || {};
                this.threshold = typeof data.threshold === "number" ? data.threshold : 0;
                this.list = Array.isArray(data.rejections) ? data.rejections : [];
            } catch (err) {
                this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
            } finally {
                this.loading = false;
            }
        },
        confirmClear(row) {
            swal.fire({
                title: this.$t("cod_rejections.clear_title"),
                text: this.$t("cod_rejections.clear_msg", { phone: row.phone }),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("cod_rejections.btn_clear"),
                cancelButtonText: this.$t("app_catalog.btn_cancel"),
                confirmButtonColor: "#fb6340",
                cancelButtonColor: "#6c757d"
            }).then(async (result) => {
                if (result.value) {
                    try {
                        await member.CodRejectionsClear(row.phone);
                        this.$notify({ type: "success", message: this.$t("cod_rejections.clear_success") });
                        this.loadList();
                    } catch (err) {
                        this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
                    }
                }
            });
        }
    }
};
</script>
